import React, { useState } from 'react';
import Link from 'next/link';
import { Button, Modal } from 'antd';
import { ClockCircleOutlined, EnvironmentOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { trackConversion } from '../../lib/conversionEvents';
import { ROUTE_LOGIN } from '../../shared/variables/routes';

// Teklif yapılamıyorsa nedeni; `href` varsa pencere teklif ekranına götürür.
export type PartnerOffer =
  | { href: string }
  | { reason: 'own' | 'expired' | 'guest' | 'outside' | 'pending' };

type Props = {
  avatar: React.ReactNode;
  largeAvatar: React.ReactNode;
  name: string;
  // Kartta avatarın altında yalnız ad (Spin gibi); tam ad pencerede ve erişilebilir adda.
  firstName: string;
  level: string | null;
  when: string;
  league: string | null;
  note: string | null;
  expired: boolean;
  offer: PartnerOffer;
};

/**
 * B2 partner ilanı kartı (Spin benzeri): ilan sahibi + boş yer, seviye, gün/saat ve lig. Karta
 * dokununca bilgi penceresi açılır; teklif oradan mevcut teklif ekranına (ilanın günü seçili) gider.
 */
export default function FeedPartnerCard({
  avatar,
  largeAvatar,
  name,
  firstName,
  level,
  when,
  league,
  note,
  expired,
  offer,
}: Props) {
  const { t } = useTranslation('leagues');
  const [open, setOpen] = useState(false);
  const show = () => {
    setOpen(true);
    trackConversion('feed_partner_open');
  };
  const close = () => setOpen(false);
  const own = 'reason' in offer && offer.reason === 'own';

  const details = (
    <>
      <p className="feed-partner-card__row">
        <ClockCircleOutlined aria-hidden="true" />
        <span>
          {when}
          {expired ? ` · ${t('detail.feed.partnerExpired')}` : ''}
        </span>
      </p>
      {league && (
        <p className="feed-partner-card__row">
          <EnvironmentOutlined aria-hidden="true" />
          <span>{league}</span>
        </p>
      )}
    </>
  );

  let primary: React.ReactNode;
  if ('href' in offer) {
    primary = (
      <Link href={offer.href} passHref>
        <Button
          type="primary"
          className="feed-partner-modal__offer"
          icon={<PlusCircleOutlined aria-hidden="true" />}
          onClick={() => trackConversion('feed_partner_offer')}
        >
          {t('detail.actions.proposeMatch')}
        </Button>
      </Link>
    );
  } else if (offer.reason === 'guest') {
    primary = (
      <Link href={ROUTE_LOGIN} passHref>
        <Button type="primary" className="feed-partner-modal__offer">
          {t('detail.feed.partnerModal.login')}
        </Button>
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`feed-partner-card${expired ? ' is-expired' : ''}`}
        aria-haspopup="dialog"
        aria-label={t('detail.feed.partnerModal.openLabel', { name, when })}
        onClick={show}
      >
        <span className="feed-partner-card__top">
          <span className="feed-partner-card__person">
            {avatar}
            <span className="feed-partner-card__name">{firstName || name}</span>
          </span>
          {/* Karşılaşma gibi: ilan sahibi VS boş yer ("?"); kendi ilanında "1 yer". */}
          <span className="feed-partner-card__versus" aria-hidden="true">
            {t('detail.feed.partnerModal.versus')}
          </span>
          <span className={`feed-partner-card__spot${own ? '' : ' is-open'}`} aria-hidden="true">
            {own ? t('detail.feed.partnerModal.spot') : '?'}
          </span>
          {level && <span className="feed-partner-card__level">{level}</span>}
        </span>
        <span className="feed-partner-card__divider" aria-hidden="true" />
        {details}
        {note && <span className="feed-partner-card__note">{note}</span>}
        <span className="feed-partner-card__more" aria-hidden="true">
          {t('detail.feed.partnerModal.more')}
        </span>
      </button>
      <Modal
        title={t('detail.feed.partnerModal.title', { name })}
        visible={open}
        onCancel={close}
        footer={null}
        width={520}
        className="feed-partner-modal"
        destroyOnClose
      >
        <div className="feed-partner-modal__person">
          {largeAvatar}
          <div>
            <p className="feed-partner-modal__name">{name}</p>
            <p className="feed-partner-modal__level">
              {t('detail.feed.partnerModal.level')}: {level || '—'}
            </p>
          </div>
        </div>
        {details}
        {note && <p className="feed-partner-modal__note">{note}</p>}
        <p className="feed-partner-modal__hint">
          {'href' in offer
            ? t('detail.feed.partnerModal.hint')
            : t(`detail.feed.partnerModal.reason.${offer.reason}`)}
        </p>
        <div className="feed-partner-modal__actions">
          {primary}
          <Button className="feed-partner-modal__close" onClick={close}>
            {t('detail.feed.partnerModal.close')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
